/**
 * Даты окна отбора — одним местом на кабинет.
 *
 * `toISOString()` ЗДЕСЬ ЗАПРЕЩЁН: он переводит дату в UTC и на нашем поясе
 * уносит её на день назад в вечерние часы — «последние 90 дней», набранные в
 * 22:00, начинались бы и кончались на день раньше. Собираем из локальных
 * частей.
 *
 * Модуль общий у окна продаж на карточке (`PeriodPicker`) и у порядка
 * листания (`productNav`): две копии этого правила разъехались бы ровно в тот
 * вечерний час, когда одну из них поправят.
 */

/** Дата в формате поля `input type="date"`, по ЛОКАЛЬНОМУ времени. */
export function iso(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

/**
 * Дата через N дней — НОВЫЙ объект, исходный не трогаем.
 *
 * `setDate` на ЛОКАЛЬНЫХ частях сам переносит конец месяца и года (30 сен + 20
 * даёт 20 окт), а в строку дату переводит только `iso()` — тем же приёмом, что
 * у `lastDays`. Прибавлять `86 400 000 мс` нельзя: в день перевода часов сутки
 * длиннее или короче, и полночь уехала бы на соседний день.
 */
export function plusDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Окно «последние N дней», ОБЕ границы включительно — как считает сервер. */
export function lastDays(days: number): [string, string] {
  const until = new Date();
  const since = new Date();
  since.setDate(since.getDate() - (days - 1));
  return [iso(since), iso(until)];
}

/** Длина окна в днях, обе границы включительно: ею подсвечивается пресет. */
export function windowDays(from: string, to: string): number {
  if (!from || !to) return 0;
  const мс = Date.parse(to) - Date.parse(from);
  return Math.round(мс / 86_400_000) + 1;
}

const SHORT_MONTHS = [
  "янв", "фев", "мар", "апр", "май", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
];

/**
 * Короткая подпись даты партии-плана: «24 сен», без года — плановые даты
 * смотрят на ближайшие недели, и год почти всегда текущий. `toLocaleDateString`
 * сюда не годится: у `ru-RU` короткий месяц идёт с точкой («24 сент.»), а
 * подпись обязана быть без неё. Разбираем ISO-строку `YYYY-MM-DD` СРЕЗАМИ, а не
 * через `new Date("2026-09-24")` — тот же запрет, что у `isValidDate`: без
 * времени конструктор берёт полночь UTC и на западных поясах отдаёт день
 * назад. `null` — партия ещё без даты.
 */
export function dayLabel(value: string | null): string {
  if (!value) return "—";
  const day = Number(value.slice(8, 10));
  const month = SHORT_MONTHS[Number(value.slice(5, 7)) - 1];
  return month ? `${day} ${month}` : value;
}

/**
 * Сезон закупки — КВАРТАЛ. Владелец назвал восемь месяцев (январь, март,
 * апрель, июнь, июль, сентябрь, октябрь, декабрь) — «это наши сезоны»
 * (16.09.26), и это четыре пары «первый — последний».
 */
const SEASON_MONTHS = [
  [0, 2, "январь — март"],
  [3, 5, "апрель — июнь"],
  [6, 8, "июль — сентябрь"],
  [9, 11, "октябрь — декабрь"],
] as const;

export type Season = {
  key: string;
  year: number;
  label: string;
  from: string;
  to: string;
  /** Сезон уже начался. Не начавшийся выбирать нечем — данных за него нет. */
  started: boolean;
};

/**
 * Четыре сезона года.
 *
 * ИДУЩИЙ СЕЗОН ОБРЕЗАЕТСЯ ПО СЕГОДНЯ: будущих дней в данных нет, и обещать их
 * подписью окна нельзя. Конец квартала берётся НУЛЕВЫМ днём следующего месяца —
 * так март кончается 31-м, а июнь 30-м без таблицы длин месяцев.
 */
export function seasons(year: number, today: Date = new Date()): Season[] {
  const сегодня = iso(today);
  return SEASON_MONTHS.map(([first, last, label]) => {
    const from = iso(new Date(year, first, 1));
    const конец = iso(new Date(year, last + 1, 0));
    return {
      key: `${year}-${first}`,
      year,
      label,
      from,
      // Строки ISO сравниваются как даты: `YYYY-MM-DD` лексикографически
      // упорядочена так же, как календарь.
      to: конец > сегодня ? сегодня : конец,
      started: from <= сегодня,
    };
  });
}

/**
 * Сезон, которому отвечает нынешнее окно: им подсвечивается строка списка и
 * открывается год группы. Год берём из самой даты начала — перебирать соседние
 * незачем, квартал начинается первым числом своего года.
 */
export function seasonOf(from: string, to: string, today: Date = new Date()): Season | null {
  const year = Number(from.slice(0, 4));
  if (!Number.isInteger(year) || year < 2000) return null;
  return seasons(year, today).find((s) => s.from === from && s.to === to) ?? null;
}

/**
 * КАЛЕНДАРНАЯ валидность даты `YYYY-MM-DD`, а не только форма строки.
 *
 * Регулярка пропускает `"2026-13-45"` — месяца 13 не бывает. `Date`
 * конструктор такие значения молча переносит на соседний месяц/год, поэтому
 * результат сверяется с тем, что в него положили: разошлось — дата не
 * календарная. Пустая строка сюда же — нативное поле даты отдаёт её, когда
 * человек чистит ввод.
 */
export function isValidDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  // Срезами, а не `split("-").map(Number)`: под `noUncheckedIndexedAccess`
  // деструктуризация массива без фиксированной длины даёт `number | undefined`
  // на каждом звене, хотя регулярка выше уже гарантирует три части.
  const y = Number(value.slice(0, 4));
  const m = Number(value.slice(5, 7));
  const d = Number(value.slice(8, 10));
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}
